import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {BaseWidgetComponent} from "../common/BaseWidget";
import {FileLoadingWorkflowEnum} from '@aiknow-srl/aik-ngx-3dviewer';
import {ThreeDimModel} from "../../../models/widget/threedimmodel/ThreeDimModel";
import {DashboardWidget} from "../../../models/dashboard/DashboardWidget";
import {DashboardService} from "../../../services/dashboard/dashboard.service";
import {UserMetricInstance} from "../../../models/user/Metric/UserMetricInstance";
import {WidgetButton} from "../common/WidgetButton";
import {GetMetricInstancesCB} from "../../../ope/metric/GetMetricInstancesCB";
import {MetricOpeService} from "../../../services/ope/metricOpe.service";
import {
  MetricTelemetryNotificationService
} from "../../../services/notification/interfaces/MetricTelemetryNotificationService";
import {NotificationService} from "../../../services/notification/notification.service";
import {UpdateMetricValueNtf} from "../../../events/notification/UpdateMetricValueNtf";
import {Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {StorageOpeService} from "../../../services/ope/storageOpe.service";
import {FileStorageDTO} from "../../../models/storage/FileStorageDTO";
import {DelFilesCB} from "../../../ope/storage/DelFilesCB";
import {ThreeDimModelFormComponent} from "./three-dim-model-form.component";

@Component({
  selector: 'app-three-dim-model',
  templateUrl: './three-dim-model-widget.component.html',
  styleUrls: ['./three-dim-model-widget.component.css']
})
export class ThreeDimModelWidgetComponent extends BaseWidgetComponent implements OnInit, AfterViewInit, OnDestroy {

  /**
   * Edit mode on/off
   */
  @Input() editMode = false;

  @Input() widgetOptions: any;
  /**
   * remove widget event
   */
  @Output() removeItem = new EventEmitter();

  /**
   * edit widget event
   */
  @Output() editItem = new EventEmitter<ThreeDimModelWidgetComponent>();

  @ViewChild('threeDimModelFormComponent') threeDimModelFormComponent?: ThreeDimModelFormComponent;
  loadingWorkflowStatus: FileLoadingWorkflowEnum = FileLoadingWorkflowEnum.INIT;
  current = 0;
  theInterval!: any;
  editingIndex?: number;
  popupOpen: boolean = false;
  modelToEdit: ThreeDimModel | undefined;
  protected readonly FileLoadingWorkflowEnum = FileLoadingWorkflowEnum;
  protected modelsToView: ThreeDimModel[] = [];
  protected currentSelectedModelIndex = 0;
  protected initializedTabs: number[] = [];
  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService: DashboardService = inject(DashboardService);
  protected metricOpeService: MetricOpeService = inject(MetricOpeService);
  protected metricTelemetryNotificationService: MetricTelemetryNotificationService = inject(NotificationService);
  protected storageOpe = inject(StorageOpeService);
  protected subscribedNotificationIds: number[] = [];
  private isCreate: boolean = false;

  private pendingUpdates = new Map<number, UpdateMetricValueNtf['value']>();
  private flushUpdates$ = new Subject<void>();

  constructor() {
    // Super class constructor call
    super();

    // Name and group of widget
    this.name = '3D Model';

  }

  ngOnInit() {
    super.ngOnInit();
    let createButton = new WidgetButton();
    createButton.clickable = true;
    createButton.icon = "add";
    createButton.editOnly = true;
    createButton.callbackFn = () => this.createModel();
    this.widgetButtons.push(createButton);
    this.modelsToView = !!this.gridsterItem.userConfig.models ? this.gridsterItem.userConfig.models : [];
    this.checkWidgetMetricsExistence();
    // this.modelsToView.push(new ThreeDimModel(this.peter, 'Peter'));


    this.subscriptions = [
      this.metricTelemetryNotificationService.onUpdateMetricValue((event: UpdateMetricValueNtf) => {
        this.pendingUpdates.set(event.value.metricId, event.value);
        this.flushUpdates$.next();
      }),
      this.flushUpdates$.pipe(debounceTime(300)).subscribe(() => {
        this.pendingUpdates.forEach((update) => {
          for (let model of this.modelsToView) {
            Object.values(model.metricsMap).forEach((metrics: UserMetricInstance[]) => {
              let metricToChange = metrics.find(x => x.id === update.metricId);
              if (!!metricToChange) {
                metricToChange.lastValue = update.value;
                metricToChange.lastValueTs = update.timestamp;
              }
            });
            let metricToChange = model.measurePanelMetrics.find(x => x.id === update.metricId);
            if (!!metricToChange) {
              metricToChange.lastValue = update.value;
              metricToChange.lastValueTs = update.timestamp;
            }
          }
        });
        this.pendingUpdates.clear();
      })
    ]
  }

  checkWidgetMetricsExistence(): void {
    if (!!this.gridsterItem.metricInstanceIds) {
      this.modelsToView.forEach((model) => {
        const parsedMeshMetricsMap = (model.metricsMap as Object);
        if (!!parsedMeshMetricsMap) {
          Object.values(parsedMeshMetricsMap).forEach((meshMetrics) => {
            const idsToDelete: number[] = [];
            meshMetrics.forEach((metric: UserMetricInstance, index: number) => {
              if (!this.gridsterItem.metricInstanceIds.includes(metric.id)) {
                idsToDelete.push(index);
              }
              idsToDelete.sort((a, b) => b - a).forEach((id) => {
                meshMetrics.splice(id, 1);
              })
            });
          });
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.initializeTabMetrics();
  }


  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.flushUpdates$.complete();
    clearInterval(this.theInterval);
    this.metricTelemetryNotificationService.removeMetricIdsToNotifications(this.subscribedNotificationIds);
  }

  getMetricInstanceIds(): number[] {
    let result: number[] = [];
    this.modelsToView.forEach((model) => {
      const parsedMeshMetricsMap = (model.metricsMap as Object);
      if (!!parsedMeshMetricsMap) {
        Object.values(parsedMeshMetricsMap).forEach((meshMetrics) => {
          meshMetrics.forEach((metric: UserMetricInstance) => {
            if (!result.includes(metric.id)) {
              result.push(metric.id);
            }
          });
        })
      }
    });
    return result;
  }

  deleteModel(theThreeDimModel: any, index: number) {
    this.showDeleteDialog(theThreeDimModel.title).show().done(
      (result: boolean) => {
        if (result) {
          let modelToDelete = this.modelsToView[index];

          if (!!modelToDelete.file) {
            let cb = new DelFilesCB();
            cb.i.files = [modelToDelete.file];
            this.storageOpe.delFiles(cb)
              .then(
                _ => {
                  caches.open(this.appService.CACHE_NAME)
                    .then(cache => {
                      return cache.delete(modelToDelete.file.objKey);
                    })
                }
              )
          }

          this.modelsToView.splice(index, 1);
          let dw = DashboardWidget.clone(this.gridsterItem);
          dw.userConfig = {models: this.modelsToView};
          const filesToStore = this.modelsToView.filter(mtv => !!mtv.file).map(mtv => mtv.file);
          dw.files = (filesToStore as FileStorageDTO[]);
          dw.metricInstanceIds = this.getMetricInstanceIds();
          if (!!this.dashboardService.selectedDashboard)
            this.dashboardService.putDashboardWidget(this.dashboardService.selectedDashboard.id, dw);
        }
      }
    );
  }

  editModel(theThreeDimModel: string, index: number): void {
    this.isCreate = false;
    this.popupOpen = true;
    this.modelToEdit = this.appService.deepCopy(this.modelsToView[index]);
  }

  createModel() {
    this.isCreate = true;
    this.popupOpen = true;
    this.modelToEdit = new ThreeDimModel();
  }

  saveModel(newModel: ThreeDimModel) {
    this.popupOpen = false;
    if (!!this.dashboardService.selectedDashboard) {
      if (!this.modelsToView) {
        this.modelsToView = [];
      }
      newModel.metricsMap = Object.fromEntries((newModel.metricsMap as Map<number, UserMetricInstance[]>).entries());
      if (!this.isCreate && this.editingIndex !== undefined) {
        this.modelsToView[this.editingIndex].metricsMap = newModel.metricsMap;
        this.modelsToView[this.editingIndex].fileName = newModel.fileName;
        this.modelsToView[this.editingIndex].file = newModel.file;
        this.modelsToView[this.editingIndex].measurePanelMetrics = newModel.measurePanelMetrics;
        this.modelsToView[this.editingIndex].loadingWorkflowStatus = newModel.loadingWorkflowStatus;
      } else {
        this.modelsToView.push(newModel);
        this.currentSelectedModelIndex = this.modelsToView.length - 1;
      }
      this.gridsterItem.userConfig.models = this.modelsToView;

      const dw = DashboardWidget.clone(this.gridsterItem);

      const filesToStore = this.modelsToView.filter(mtv => !!mtv.file).map(mtv => mtv.file);
      dw.files = (filesToStore as FileStorageDTO[]);
      dw.metricInstanceIds = this.getMetricInstanceIds();
      this.dashboardService.putDashboardWidget(this.dashboardService.selectedDashboard?.id, dw).then(
        () => this.initializeTabMetrics(this.editingIndex)
      );
    }
  }

  getMapForTDViewer(metricsMap: Object) {
    let metricEntries: [any, any][] = Object.entries(metricsMap);

    metricEntries.forEach((entry) => {
      if (!isNaN(entry[0]) && entry[0] !== null) {
      }
      entry[0] = Number(entry[0])
    });

    return new Map(metricEntries);
  }

  initializeTabMetrics(indexToInit?: number) {

    if (indexToInit === undefined) {
      indexToInit = this.currentSelectedModelIndex;
    }

    if (!!this.modelsToView[indexToInit]) {

      let cb = new GetMetricInstancesCB();
      cb.i.metricInstanceIds = [];

      Object.values(this.modelsToView[indexToInit].metricsMap).forEach(
        (metrics: UserMetricInstance[]) => {
          cb.i.metricInstanceIds = cb.i.metricInstanceIds.concat(metrics.map(x => x.id));
        }
      );

      cb.i.metricInstanceIds = cb.i.metricInstanceIds.concat(
        this.modelsToView[indexToInit].measurePanelMetrics.map(x => x.id)
      );

      if (!this.initializedTabs.includes(indexToInit)) {

        this.subscribedNotificationIds = this.subscribedNotificationIds.concat(cb.i.metricInstanceIds);
        this.metricTelemetryNotificationService.addMetricIdsToNotifications(cb.i.metricInstanceIds);
        this.initializedTabs.push(indexToInit);

        this.metricOpeService.getMetricInstances(cb).then(() => {
            Object.values(this.modelsToView[indexToInit!].metricsMap).forEach(
              (metrics: UserMetricInstance[]) => {
                metrics.forEach((metric) => {
                  const fullMetric = cb.o.metricInstances.find(x => x.id === metric.id);
                  if (!!fullMetric) {
                    metric.lastValue = fullMetric.lastValue;
                    metric.lastValueTs = fullMetric.lastValueTs;
                    metric.decimalDigits = fullMetric.decimalDigits;
                    metric.dataUnit = fullMetric.dataUnit;
                    metric.deviceName = fullMetric.deviceName;
                    metric.partitionName = fullMetric.partitionName;
                    metric.name = fullMetric.name;
                  }
                })
              }
            );

            Object.values(this.modelsToView[indexToInit!].measurePanelMetrics).forEach(
              (metric) => {
                const fullMetric = cb.o.metricInstances.find(x => x.id === metric.id);
                if (!!fullMetric) {
                  metric.lastValue = fullMetric.lastValue;
                  metric.lastValueTs = fullMetric.lastValueTs;
                  metric.decimalDigits = fullMetric.decimalDigits;
                  metric.dataUnit = fullMetric.dataUnit;
                  metric.deviceName = fullMetric.deviceName;
                  metric.partitionName = fullMetric.partitionName;
                  metric.name = fullMetric.name;
                }
              }
            );
          }
        );
      }
    }
  }

  dispatchResizeEvent() {
    window.dispatchEvent(new Event('resize'));
  }

  showMetricPanel(theThreeDimModel: ThreeDimModel) {
    return this.loadingWorkflowStatus === FileLoadingWorkflowEnum.COMPLETED
      && !(!theThreeDimModel.measurePanelMetrics || theThreeDimModel.measurePanelMetrics.length === 0)
      && !this.appService.isSmallScreen();

  }

}
